import React, { forwardRef } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Typography,
    Divider
} from '@mui/material';

const DailyTaskPrint = forwardRef(({ 
    processedTasks = [], 
    propertyName = '', 
    shiftName = '',
    workRecvDate = '',
    searchCriteria = '',
    title = 'Daily Task Allocation Report'
}, ref) => {
    
    console.log('Processed Tasks for Printing:', processedTasks);

    const formatDate = (dateString) => {
        if (!dateString) return '________';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getDayFromDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return days[date.getDay()];
    };

    const formattedDate = formatDate(workRecvDate);
    const dayName = getDayFromDate(workRecvDate);

    const groupedByYojana = processedTasks.reduce((acc, task) => {
        const yojanaKey = task.yojanaName || 'Uncategorized';
        if (!acc[yojanaKey]) {
            acc[yojanaKey] = {
                yojanaName: yojanaKey,
                areas: {}
            };
        }
        
        const areaKey = task.areaName;
        if (!acc[yojanaKey].areas[areaKey]) {
            acc[yojanaKey].areas[areaKey] = {
                areaName: task.areaName,
                tasks: []
            };
        }
        
        acc[yojanaKey].areas[areaKey].tasks.push(task);
        return acc;
    }, {});

    const yojanaGroups = Object.values(groupedByYojana);

    const YojanaTable = ({ yojanaGroup, yojanaIndex }) => {
        const areaGroups = Object.values(yojanaGroup.areas);
        
        return (
            <Box key={yojanaIndex} sx={{ 
                pageBreakAfter: 'always',
                marginBottom: '20px',
                '@media print': {
                    pageBreakAfter: 'always',
                    pageBreakInside: 'avoid'
                }
            }}>
               
                <Box sx={{ 
                    textAlign: 'center', 
                    marginBottom: '6px',
                    paddingBottom: '4px'
                }}>
                    <Typography variant="h6" sx={{ 
                        fontWeight: 'bold',
                        fontSize: '14px',
                        marginBottom: '2px',
                        lineHeight: 1.2
                    }}>
                        {yojanaGroup.yojanaName || title}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                        fontSize: '12px',
                        lineHeight: 1.1
                    }}>
                        <strong>Property:</strong> {propertyName} || <strong>Shift:</strong> {shiftName}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                        fontSize: '12px',
                        lineHeight: 1.1,
                        color: '#333',
                        fontWeight: 'bold',
                        marginTop: '2px'
                    }}>
                        <strong>Work Receiving Date:</strong> {formattedDate} ({dayName})
                    </Typography>
                </Box>

                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginBottom: '6px',
                    fontSize: '13px',
                    fontWeight: 'bold'
                }}>
                    <Typography variant="body2" sx={{ fontSize: '11px' }}>
                        <strong>Staff Name:</strong> ______________
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '11px' }}>
                        <strong>Issue Date:</strong> {new Date().toLocaleDateString()}
                    </Typography>
                </Box>

                {areaGroups.length === 0 ? (
                    <Box sx={{ 
                        textAlign: 'center', 
                        padding: '20px',
                        fontSize: '12px',
                        color: '#666'
                    }}>
                        <Typography>No tasks for {dayName}</Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table 
                            size="small" 
                            sx={{ 
                                width: '100%',
                                borderCollapse: 'collapse',
                                '& .MuiTableCell-root': {
                                    border: '1px solid #000',
                                    padding: '4px 6px',
                                    fontSize: '10px',
                                    lineHeight: 1.2
                                }
                            }}
                        >
                            <TableHead>
                                <TableRow sx={{ 
                                    backgroundColor: '#f0f0f0'
                                }}>
                                    <TableCell 
                                        align="center"
                                        sx={{ 
                                            fontWeight: 'bold',
                                            width: '50px',
                                            fontSize: '11px'
                                        }}
                                    >
                                        S.No.
                                    </TableCell>
                                    <TableCell 
                                        align="center"
                                        sx={{ 
                                            fontWeight: 'bold',
                                            width: '100px',
                                            fontSize: '11px'
                                        }}
                                    >
                                        Area
                                    </TableCell>
                                    <TableCell 
                                        align="center"
                                        sx={{ 
                                            fontWeight: 'bold',
                                            width: '200px',
                                            fontSize: '11px'
                                        }}
                                    >
                                        Task
                                    </TableCell>
                                    <TableCell 
                                        align="center"
                                        sx={{ 
                                            fontWeight: 'bold',
                                            width: '80px',
                                            fontSize: '11px'
                                        }}
                                    >
                                        Plan
                                    </TableCell>
                                    <TableCell 
                                        align="center"
                                        sx={{ 
                                            fontWeight: 'bold',
                                            width: '80px',
                                            fontSize: '11px'
                                        }}
                                    >
                                        Actual
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {areaGroups.map((areaGroup, areaIndex) => (
                                    <React.Fragment key={areaIndex}>
                                        {areaGroup.tasks.map((task, taskIndex) => {
                                            // Calculate serial number
                                            const serialNo = areaGroups
                                                .slice(0, areaIndex)
                                                .reduce((sum, area) => sum + area.tasks.length, 0) + taskIndex + 1;
                                            
                                            return (
                                                <TableRow key={`${areaIndex}-${taskIndex}`}>
                                                    <TableCell align="center">
                                                        {serialNo}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                                        {areaGroup.areaName}
                                                    </TableCell>
                                                    <TableCell>
                                                        {task.taskName}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{
                                                            width: '15px',
                                                            height: '15px',
                                                            border: '1px solid #000',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            margin: 'auto',
                                                            backgroundColor: task.planDay ? '#e8f5e9' : 'transparent'
                                                        }}>
                                                            {task.planDay ? '✓' : ''}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{
                                                            width: '15px',
                                                            height: '15px',
                                                            border: '1px solid #000',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            margin: 'auto',
                                                            backgroundColor: task.actualDay ? '#e3f2fd' : 'transparent'
                                                        }}>
                                                            {task.actualDay ? '✓' : ''}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </React.Fragment>
                                ))}

                                <TableRow sx={{ 
                                    backgroundColor: '#f9f9f9',
                                    fontWeight: 'bold'
                                }}>
                                    <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                                        Total Tasks:
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                        {areaGroups.reduce((sum, area) => sum + area.tasks.length, 0)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                        {/* Count of actual checked tasks */}
                                        {areaGroups.reduce((sum, area) => 
                                            sum + area.tasks.filter(task => task.actualDay).length, 0
                                        )}
                                    </TableCell>
                                </TableRow>

                                {/* Auditor Sign Row */}
                                <TableRow sx={{ 
                                    backgroundColor: '#f9f9f9'
                                }}>
                                    <TableCell colSpan={5} align="center" sx={{ fontWeight: 'bold', padding: '8px' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">Auditor Sign: __________________</Typography>
                                            <Typography variant="body2">Date: ________/________/________</Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Box sx={{ 
                    marginTop: '8px',
                    paddingTop: '4px',
                    borderTop: '1px solid #ccc',
                    fontSize: '8px',
                    color: '#666',
                    display: 'flex',
                    justifyContent: 'space-between'
                }}>
                    <Typography variant="caption" sx={{ fontSize: '8px' }}>
                        Printed: {new Date().toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '8px' }}>
                        Page: {yojanaIndex + 1} of {yojanaGroups.length}
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <Box ref={ref} sx={{ 
            padding: '0px',
            backgroundColor: 'white',
            '@media print': {
                padding: '0px',
                '@page': {
                    size: 'A4',
                    margin: '5mm'
                }
            }
        }}>
            <Box sx={{ 
                padding: '5mm',
                minHeight: 'auto',
                width: '100%'
            }}>
                {yojanaGroups.length === 0 ? (
                    <Box sx={{ 
                        textAlign: 'center', 
                        padding: '20px',
                        fontSize: '12px',
                        color: '#666'
                    }}>
                        <Typography>No tasks for {dayName}</Typography>
                    </Box>
                ) : (
                    <>
                        {yojanaGroups.map((yojanaGroup, index) => (
                            <YojanaTable 
                                key={index} 
                                yojanaGroup={yojanaGroup} 
                                yojanaIndex={index} 
                            />
                        ))}
                    </>
                )}
            </Box>
        </Box>
    );
});

DailyTaskPrint.displayName = 'DailyTaskPrint';

export default DailyTaskPrint;